import React, { useState } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { useMenu } from '../hooks/useMenu';
import { Menu, Bell, Sun, Moon, User, Shield, LayoutDashboard, LogOut, ChevronRight, Settings as SettingsIcon, Award, GitBranch, ArrowUp, ArrowLeft, Cloud } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../hooks/useAuth';
import { LogoutModal } from './LogoutModal';
import { SignedImage } from './SignedImage';
import { cn } from '../lib/utils';

export const Layout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { menu } = useMenu();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogout = async () => {
    setIsLogoutModalOpen(false);
    await signOut();
  };

  // Breadcrumb logic
  const pathSegments = location.pathname.split('/').filter(Boolean);
  
  const getBreadcrumbs = () => {
    const crumbs = [{ label: 'Home', path: '/' }];
    
    let currentPath = '';
    pathSegments.forEach((segment, idx) => {
      currentPath += `/${segment}`;
      
      let label = segment.charAt(0).toUpperCase() + segment.slice(1);
      
      // Custom labels for specific segments
      if (segment === 'user') label = 'Utente';
      if (segment === 'seggen') label = 'Segreteria Generale';
      if (segment === 'cd') label = 'Consiglio Direttivo';
      if (segment === 'zona_bologna') label = 'Zona Bologna';
      if (segment === 'dashboard' && pathSegments[idx-1] === 'user') label = 'Dashboard';
      if (segment === 'preferenze') label = 'Preferenze';
      if (segment === 'ruoli') label = 'Ruoli';
      
      crumbs.push({ label, path: currentPath });
    });
    
    return crumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  const segments = location.pathname.split('/').filter(Boolean);
  const parentPath = segments.length > 1 ? '/' + segments.slice(0, -1).join('/') : '/';
  const isAtHome = location.pathname === '/';
  const canGoBack = typeof window !== 'undefined' && window.history?.state?.idx > 0;

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <Sidebar 
        isOpen={isSidebarOpen} 
        onToggle={toggleSidebar} 
      />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b bg-card z-30">
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleSidebar}
              className={cn(
                "p-2 rounded-lg hover:bg-accent transition-colors",
                isSidebarOpen && "hidden lg:flex"
              )}
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <nav className="hidden md:flex items-center space-x-2 text-sm text-muted-foreground">
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <ChevronRight className="w-4 h-4" />}
                  {idx === breadcrumbs.length - 1 ? (
                    <span className="text-foreground font-medium truncate max-w-[200px]">
                      {crumb.label}
                    </span>
                  ) : (
                    <Link 
                      to={crumb.path} 
                      className="hover:text-foreground transition-colors truncate max-w-[150px]"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>

           <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 mr-2">
              <button
                disabled={!canGoBack}
                onClick={() => navigate(-1)}
                className={cn(
                  "p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground relative group",
                  !canGoBack && "opacity-30 cursor-not-allowed hover:bg-transparent text-muted-foreground/30"
                )}
                title="Indietro"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <button
                disabled={isAtHome}
                onClick={() => {
                  const isPersonDetail = location.pathname.startsWith('/segreteria/anagrafica/persone/') && location.pathname !== '/segreteria/anagrafica/persone';
                  if (isPersonDetail) {
                    sessionStorage.setItem('persone_map_view_mode', 'list');
                  }
                  navigate(parentPath);
                }}
                className={cn(
                  "p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground relative group",
                  isAtHome && "opacity-30 cursor-not-allowed hover:bg-transparent text-muted-foreground/30"
                )}
                title="Su di un livello"
              >
                <ArrowUp className="w-5 h-5" />
              </button>
            </div>

            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg hover:bg-accent transition-colors"
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button className="p-2 rounded-lg hover:bg-accent transition-colors relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-destructive rounded-full" />
            </button>
            <div className="h-8 w-px bg-border mx-2" />
            
            {/* User Menu */}
            <div className="relative">
              <button 
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-3 pl-2 p-1 rounded-xl hover:bg-accent transition-colors"
              >
                <div className="flex flex-col items-end hidden sm:flex">
                  <span className="text-sm font-bold">{user?.full_name}</span>
                  <span className="text-xs text-muted-foreground">{user?.role}</span>
                </div>
                {user?.avatar_url ? (
                  <div className="w-10 h-10 rounded-full border overflow-hidden bg-slate-100 flex items-center justify-center">
                    <SignedImage 
                      path={user.avatar_url} 
                      alt={user.full_name} 
                      className="w-full h-full object-cover"
                      fallback={
                        user.avatar_url?.startsWith('http') ? (
                          <img src={user.avatar_url} alt={user.full_name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <User className="w-6 h-6 text-slate-300" />
                        )
                      }
                    />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <User className="w-6 h-6" />
                  </div>
                )}
              </button>

              <AnimatePresence>
                {isUserMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsUserMenuOpen(false)} 
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-56 bg-card border rounded-2xl shadow-xl z-50 overflow-hidden"
                    >
                      <div className="p-2 space-y-1">
                        <button 
                          onClick={() => { navigate('/user/profilo'); setIsUserMenuOpen(false); }}
                          className="flex items-center w-full px-4 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                        >
                          <User className="w-4 h-4 mr-3" />
                          Profilo
                        </button>
                        <button 
                          onClick={() => { navigate('/user/ruoli'); setIsUserMenuOpen(false); }}
                          className="flex items-center w-full px-4 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                        >
                          <Shield className="w-4 h-4 mr-3" />
                          Ruoli
                        </button>
                        <button 
                          onClick={() => { navigate('/user/dashboard'); setIsUserMenuOpen(false); }}
                          className="flex items-center w-full px-4 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                        >
                          <LayoutDashboard className="w-4 h-4 mr-3" />
                          Mia Dashboard
                        </button>
                        <button 
                          onClick={() => { navigate('/user/workspace'); setIsUserMenuOpen(false); }}
                          className="flex items-center w-full px-4 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                        >
                          <Cloud className="w-4 h-4 mr-3" />
                          Google Workspace
                        </button>
                        <button 
                          onClick={() => { navigate('/user/versione'); setIsUserMenuOpen(false); }}
                          className="flex items-center w-full px-4 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                        >
                          <GitBranch className="w-4 h-4 mr-3" />
                          Versione
                        </button>
                        <button 
                          onClick={() => { navigate('/user/preferenze'); setIsUserMenuOpen(false); }}
                          className="flex items-center w-full px-4 py-2 text-sm rounded-lg hover:bg-accent transition-colors"
                        >
                          <SettingsIcon className="w-4 h-4 mr-3" />
                          Preferenze
                        </button>
                        <div className="h-px bg-border my-1" />
                        <button 
                          onClick={() => { setIsLogoutModalOpen(true); setIsUserMenuOpen(false); }}
                          className="flex items-center w-full px-4 py-2 text-sm text-destructive rounded-lg hover:bg-destructive/10 transition-colors"
                        >
                          <LogOut className="w-4 h-4 mr-3" />
                          Uscita
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto px-6 pb-6 pt-[2px]">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col h-full"
          >
            <Outlet />
          </motion.div>
        </main>
      </div>

      <LogoutModal 
        isOpen={isLogoutModalOpen} 
        onClose={() => setIsLogoutModalOpen(false)} 
        onConfirm={handleLogout} 
      />
    </div>
  );
};
